import React, { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import { Parser } from "json2csv";
import { format, parseISO } from "date-fns";
import {
  AddStaffModal,
  ViewStaffModal,
  EditStaffModal,
  DeleteStaffModal,
  ViewStaffInfoModal,
  EnableStaffModal,
  DisableStaffModal,
  StaffStatusModal,
} from "../components/modals/staffModal";
import { getAuthToken, getAdminName, getAdminProfileData } from "../utils/cookieUtils";

function StaffManagement() {
  const popupRef = useRef(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [viewModalIsOpen, setViewModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteSuccessModalIsOpen, setDeleteSuccessModalIsOpen] =
    useState(false);
  const [disableSuccessModalIsOpen, setDisableSuccessModalIsOpen] =
    useState(false);
  const [enableSuccessModalIsOpen, setEnableSuccessModalIsOpen] =
    useState(false);
  const [editSuccessModalIsOpen, setEditSuccessModalIsOpen] = useState(false);
  const [optionsPopupOpen, setOptionsPopupOpen] = useState(null);
  const [passwordSuccessModalIsOpen, setPasswordSuccessModalIsOpen] =
    useState(false);
  const [viewStaffInfo, setViewStaffInfo] = useState(null);
  const adminData = getAdminName();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = useMemo(() => getAuthToken(), []);
  const adminProfile = getAdminProfileData()?.message?.admin;

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: "/super-admin",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }),
    [token]
  );
  const [formData, setFormData] = useState({
    f_name: "",
    l_name: "",
    address: "",
    email: "",
    phone: "",
    nin: "",
    role_id: "2", 
    location_id: "", 
  });
  const [admins, setAdmins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    next_page_url: null,
    prev_page_url: null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; 
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState(""); 
  const [searchTerm, setSearchTerm] = useState(""); 
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminToDeleteId, setAdminToDeleteId] = useState(null); 
  const [passwordInput, setPasswordInput] = useState("");
  const [disableModalIsOpen, setDisableModalIsOpen] = useState(false);
  const [disableUserId, setDisableUserId] = useState(null);
  const [enableModalIsOpen, setEnableModalIsOpen] = useState(false);
  const formattedDate = useMemo(() => {
    const date = new Date();
    return format(date, "EEE, MMMM dd");
  }, []);

  useEffect(() => {
    const fetchAdmins = async (page = 1, searchValue = "", locationId = "", roleId = "") => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: itemsPerPage.toString()
        });
        
        if (searchValue.trim()) {
          // Check if the search value looks like an email (contains @)
          if (searchValue.includes('@')) {
            params.append('email', searchValue.trim());
          } else {
            params.append('name', searchValue.trim());
          }
        }

        if (locationId) {
          params.append('location_id', locationId);
        }

        if (roleId) {
          params.append('role_id', roleId);
        }

        const response = await axiosInstance.get(`/get-admins?${params}`);
        
        setAdmins(response.data.message.data);
        setPaginationData({
          current_page: response.data.message.current_page,
          last_page: response.data.message.last_page,
          per_page: response.data.message.per_page,
          total: response.data.message.total,
          next_page_url: response.data.message.next_page_url,
          prev_page_url: response.data.message.prev_page_url
        });
      } catch (error) {
        console.error(
          "Error fetching admins:",
          error.response ? error.response.data : error.message
        );
        toast.error("Failed to load staff members. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdmins(currentPage, searchTerm, selectedLocationId, selectedRoleId);
  }, [axiosInstance, currentPage, searchTerm, selectedLocationId, selectedRoleId, itemsPerPage]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/locations', {
          params: { per_page: 1000 }
        });
        setLocations(response.data?.data?.data || []);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setLocations([]);
        toast.error("Failed to load locations.");
      }
    };

    fetchLocations();
  }, [axiosInstance]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_image") {
      const file = files[0];
      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("f_name", formData.f_name);
      formDataToSend.append("l_name", formData.l_name);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("nin", formData.nin);
      formDataToSend.append("role_id", formData.role_id);
      formDataToSend.append("location_id", formData.location_id);
      if (selectedImage) {
        formDataToSend.append("profile_image", selectedImage);
      }

      const response = await axiosInstance.post(
        "/create-admin-account",
        formDataToSend
      );
      setLoginId(response.data.message.login_id);
      setPassword(response.data.message.password);
      setFormData({
        f_name: "",
        l_name: "",
        address: "",
        email: "",
        phone: "",
        nin: "",
        role_id: "",
        location_id: "",
      });
      setSelectedImage(null);
      setImagePreviewUrl(null);
      setModalIsOpen(false);
      setSuccessModalIsOpen(true);
    } catch (error) {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : error.message;
      console.error("Error submitting form:", errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleShare = () => {
    const text = `Login ID: ${loginId}\nPassword: ${password}`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "staff_credentials.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShareDetails = () => {
    if (viewStaffInfo) {
      const text = `
      First Name: ${selectedAdmin.f_name}\n
      Last Name: ${selectedAdmin.l_name}\n
      Email: ${viewStaffInfo.email}\n
      Phone Number: ${viewStaffInfo.phone}\n
      Home Address: ${viewStaffInfo.address}\n
      National ID: ${viewStaffInfo.nin}\n
      Role: ${viewStaffInfo.role_id}\n
      Password: ${viewStaffInfo.plain_password}
    `;
      const blob = new Blob([text], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "staff_details.txt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      
      let allAdmins = [];
      let currentPage = 1;
      let hasMorePages = true;
      
      while (hasMorePages) {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          per_page: '100'
        });
        
        if (searchTerm.trim()) {
          // Check if the search value looks like an email (contains @)
          if (searchTerm.includes('@')) {
            params.append('email', searchTerm.trim());
          } else {
            params.append('name', searchTerm.trim());
          }
        }
        
        if (selectedLocationId) {
          params.append('location_id', selectedLocationId);
        }
        
        if (selectedRoleId) {
          params.append('role_id', selectedRoleId);
        }
        
        const response = await axiosInstance.get(`/get-admins?${params}`);
        const pageData = response.data.message.data;
        allAdmins = [...allAdmins, ...pageData];
        
        hasMorePages = response.data.message.next_page_url !== null;
        currentPage++;
        
        if (currentPage > 100) break;
      }
      
      if (allAdmins.length === 0) {
        toast.info("No data available to export");
        return;
      }

      const fields = [
        "f_name",
        "l_name", 
        "email",
        "role_id",
        "created_at",
        "last_seen",
        "access_status",
      ];
      const opts = { fields };
      const parser = new Parser(opts);
      const csv = parser.parse(allAdmins);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "admins.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${allAdmins.length} records successfully`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = () => {
    if (paginationData.next_page_url && currentPage < paginationData.last_page) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (paginationData.prev_page_url && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const debounceSearch = useMemo(() => {
    const delaySearch = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  useEffect(() => {
    debounceSearch();
  }, [searchTerm, debounceSearch]);

  const handleLocationFilter = (locationId) => {
    setSelectedLocationId(locationId);
    setCurrentPage(1);
    setLocationDropdownOpen(false);
  };

  const handleRoleFilter = (roleId) => {
    setSelectedRoleId(roleId);
    setCurrentPage(1);
    setRoleDropdownOpen(false);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return "N/A";
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : "Unknown";
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      f_name: admin.f_name,
      l_name: admin.l_name,
      address: admin.address,
      email: admin.email,
      phone: admin.phone,
      nin: admin.nin,
      role_id: admin.role_id,
      location_id: admin.location_id,
    });
    setEditModalIsOpen(true);
    setOptionsPopupOpen(null);
  };

  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setAdminToDeleteId(admin.id);
    setDeleteModalIsOpen(true);
    setOptionsPopupOpen(null);
  };

  const openOptionsPopup = (index) => {
    setOptionsPopupOpen(optionsPopupOpen === index ? null : index);
  };

  const closeOptionsPopup = () => {
    setOptionsPopupOpen(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closeOptionsPopup();
      }
      // Close dropdowns when clicking outside
      if (locationDropdownOpen && !event.target.closest('.location-filter')) {
        setLocationDropdownOpen(false);
      }
      if (roleDropdownOpen && !event.target.closest('.role-filter')) {
        setRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef, locationDropdownOpen, roleDropdownOpen]);

  const openDisableModal = (admin) => {
    setSelectedAdmin(admin);
    setDisableUserId(admin.id); // Set the user ID to disable
    setDisableModalIsOpen(true);
    setOptionsPopupOpen(null);
  };

  const handleDisableUser = async () => {
    try {
      if (!disableUserId) {
        console.error("User ID to disable is not set.");
        return;
      }

      const response = await axiosInstance.post(`/disable-user-access`, {
        user_id: disableUserId,
      });

      setDisableModalIsOpen(false);
      setDisableSuccessModalIsOpen(true);
    } catch (error) {
      console.error(
        "Error disabling user:",
        error.response ? error.response.data : error.message
      );
      toast.error("Failed to disable user");
    }
  };

  const openEnableModal = (admin) => {
    setSelectedAdmin(admin);
    setEnableModalIsOpen(true);
    setOptionsPopupOpen(null);
  };

  const handleEnableUser = async () => {
    try {
      if (!selectedAdmin || !selectedAdmin.id) {
        console.error("User ID to enable is not set.");
        return;
      }

      const response = await axiosInstance.post(`/enable-user-access`, {
        user_id: selectedAdmin.id,
      });

      setEnableModalIsOpen(false);
      setEnableSuccessModalIsOpen(true);
    } catch (error) {
      console.error(
        "Error enabling user:",
        error.response ? error.response.data : error.message
      );
      toast.error("Failed to enable user");
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      if (!selectedAdmin || !selectedAdmin.id) {
        console.error("Admin ID to delete is not set.");
        return;
      }
      const response = await axiosInstance.post("/delete-admin", {
        user_id: selectedAdmin.id,
      });

      const updatedAdmins = admins.filter(
        (admin) => admin.id !== selectedAdmin.id
      );
      setAdmins(updatedAdmins);
      setDeleteModalIsOpen(false);
      setDeleteSuccessModalIsOpen(true);
    } catch (error) {
      console.error(
        "Error deleting admin:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!selectedAdmin || !selectedAdmin.id) {
        console.error("Selected admin or admin ID is missing.");
        return;
      }

      // Construct updatedData with all fields, prioritizing formData
      const updatedData = {
        user_id: selectedAdmin.id,
        f_name:
          formData.f_name === undefined
            ? selectedAdmin.f_name
            : formData.f_name,
        l_name:
          formData.l_name === undefined
            ? selectedAdmin.l_name
            : formData.l_name,
        address:
          formData.address === undefined
            ? selectedAdmin.address
            : formData.address,
        email:
          formData.email === undefined ? selectedAdmin.email : formData.email,
        phone:
          formData.phone === undefined ? selectedAdmin.phone : formData.phone,
        nin: formData.nin === undefined ? selectedAdmin.nin : formData.nin,
        role_id:
          formData.role_id === undefined
            ? selectedAdmin.role_id
            : formData.role_id,
        location_id:
          formData.location_id === undefined
            ? selectedAdmin.location_id
            : formData.location_id,
      };

      const response = await axiosInstance.post(
        "/update-user-details",
        updatedData
      );

      // Update admins state to reflect the changes
      const updatedAdmins = admins.map((admin) =>
        admin.id === selectedAdmin.id ? { ...admin, ...updatedData } : admin
      );
      setAdmins(updatedAdmins);

      setEditModalIsOpen(false);
      setEditSuccessModalIsOpen(true); // Open the success modal
    } catch (error) {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : error.message;
      console.error("Error updating user details:", errorMessage);
      toast.error(errorMessage);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordInput(e.target.value);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post("/view-staff-info", {
        staff_id: selectedAdmin.id,
        password: passwordInput,
      });
      const staffInfo = response.data.message.staff;
      setViewStaffInfo(staffInfo); // Set the viewStaffInfo state with the response data
      setViewModalIsOpen(false);
      setPasswordSuccessModalIsOpen(true); // Open the success modal
      setPasswordInput("");
    } catch (error) {
      console.error(
        "Error validating password:",
        error.response ? error.response.data : error.message
      );
      toast.error("Invalid password");
    }
  };

  const openViewModal = (admin) => {
    setSelectedAdmin(admin);
    setViewModalIsOpen(true);
    setOptionsPopupOpen(null);
  };

  // No need for paginatedAdmins since we're using server-side pagination
  // The admins array already contains only the current page data

  const generatePageNumbers = () => {
    const totalPages = paginationData.last_page;
    const pageNumbers = [];

    // If we have fewer than or equal to 5 total pages, display them all
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always add the first page
      pageNumbers.push(1);

      // Show ellipsis if we're more than 2 pages away from the current page
      if (currentPage > 3) {
        pageNumbers.push("...");
      }

      // Add pages around the current page (max 2 pages on either side)
      const start = Math.max(currentPage - 2, 2);
      const end = Math.min(currentPage + 2, totalPages - 1);
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      // Show ellipsis if we're more than 2 pages away from the last page
      if (currentPage < totalPages - 2) {
        pageNumbers.push("...");
      }

      // Always add the last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handlePageClick = (page) => {
    if (page !== "...") {
      setCurrentPage(page);
    }
  };

  return (
    <div className="dashboard">
      <div className="Nav">
        <Sidebar />
      </div>
      <div className="dashboard-display">
        <div className="top-bar container-fluid">
          <div className="top-bar-info">
            <p id="date">{formattedDate}</p>
            <b>
              <p>Welcome back {adminData}</p>
            </b>
          </div>
          <div className="top-bar-button">
            <button onClick={() => setModalIsOpen(true)}>
              <img
                src={`${process.env.PUBLIC_URL}/images/user-add.png`}
                alt="Add staff"
              />{" "}
              Add Staff
            </button>
          </div>
        </div>

        <div className="main-settings staff-items">
          <div className="search">
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="export-group">
            {/* Location Filter - Only for Super Admin */}
            {adminProfile?.role_id === 3 && (
              <div className="location-filter">
                <button
                  className={`location-filter-button ${selectedLocationId ? 'active' : ''}`}
                  onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                >
                  <span>
                    {selectedLocationId 
                      ? locations.find(loc => loc.id.toString() === selectedLocationId)?.name || 'Select Location'
                      : 'All Locations'
                    }
                  </span>
                  <span className={`location-filter-arrow ${locationDropdownOpen ? 'open' : ''}`}>
                      <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                </button>
                {locationDropdownOpen && (
                  <div className="location-filter-dropdown">
                    <div
                      className={`location-filter-option ${!selectedLocationId ? 'selected' : ''}`}
                      onClick={() => handleLocationFilter("")}
                    >
                      All Locations
                    </div>
                    {locations.map(location => (
                      <div
                        key={location.id}
                        className={`location-filter-option ${selectedLocationId === location.id.toString() ? 'selected' : ''}`}
                        onClick={() => handleLocationFilter(location.id.toString())}
                      >
                        {location.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Role Filter */}
            <div className="role-filter">
              <button
                className={`role-filter-button ${selectedRoleId ? 'active' : ''}`}
                onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
              >
                <span>
                  {selectedRoleId === "2" ? "Admin" 
                   : selectedRoleId === "4" ? "Branch Admin"
                   : selectedRoleId === "3" ? "Super-Admin"
                   : "All Roles"
                  }
                </span>
                <span className={`role-filter-arrow ${roleDropdownOpen ? 'open' : ''}`}>
                  <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              {roleDropdownOpen && (
                <div className="role-filter-dropdown">
                  <div
                    className={`role-filter-option ${!selectedRoleId ? 'selected' : ''}`}
                    onClick={() => handleRoleFilter("")}
                  >
                    All Roles
                  </div>
                  <div
                    className={`role-filter-option ${selectedRoleId === "3" ? 'selected' : ''}`}
                    onClick={() => handleRoleFilter("3")}
                  >
                    Super-Admin
                  </div>
                  <div
                    className={`role-filter-option ${selectedRoleId === "2" ? 'selected' : ''}`}
                    onClick={() => handleRoleFilter("2")}
                  >
                    Admin
                  </div>
                  <div
                    className={`role-filter-option ${selectedRoleId === "4" ? 'selected' : ''}`}
                    onClick={() => handleRoleFilter("4")}
                  >
                    Branch Admin
                  </div>
                </div>
              )}
            </div>

            <div className="export">
              <button onClick={handleExportCSV}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                  alt="Export"
                />{" "}
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="show-admin-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Location</th>
                <th>Date Created</th>
                <th>Last Active</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9">
                    <div className="spinner"></div>
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center" }}>
                    No staff found
                  </td>
                </tr>
              ) : (
                admins.map((item, index) => {
                  const serialNumber =
                    (currentPage - 1) * itemsPerPage + index + 1;
                  return (
                    <tr key={index}>
                      <td>{serialNumber}</td>
                      <td>{`${item.f_name} ${item.l_name}`}</td>
                      <td>{item.email}</td>
                      <td>{item.role_id === 2 ? "Admin" : item.role_id === 4 ? "Branch Admin" : "Super-Admin"}</td>
                      <td>{getLocationName(item.location_id)}</td>
                      <td>
                        {item.created_at
                          ? format(parseISO(item.created_at), "dd MMM yyyy")
                          : "N/A"}
                      </td>
                      <td>
                        {item.last_seen
                          ? format(parseISO(item.last_seen), "dd MMM yyyy")
                          : "N/A"}
                      </td>
                      <td
                        style={{
                          color: item.access_status === 1 ? "green" : "red",
                          fontWeight: "700",
                        }}
                      >
                        {item.access_status === 1 ? "Active" : "Inactive"}
                      </td>
                      <td>
                        <div className="options-menu">
                          <img
                            src={`${process.env.PUBLIC_URL}/images/horizontal-menu.png`}
                            onClick={() => openOptionsPopup(index)}
                            alt="Options menu"
                          />
                          {optionsPopupOpen === index && (
                            <div className="popup" ref={popupRef}>
                              <ul>
                                <li onClick={() => openEditModal(item)}>
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/staff-edit.png`}
                                    alt="Edit"
                                  />{" "}
                                  Edit Staff Profile
                                </li>
                                <li onClick={() => openViewModal(item)}>
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/staff-view.png`}
                                    alt="View"
                                  />{" "}
                                  View Staff Profile
                                </li>
                                {item.access_status === 0 && (
                                  <li onClick={() => openEnableModal(item)}>
                                    <img
                                      src={`${process.env.PUBLIC_URL}/images/enable-user.png`}
                                      alt="Enable"
                                    />{" "}
                                    Enable Staff Profile
                                  </li>
                                )}

                                {item.access_status === 1 && (
                                  <li
                                    style={{ color: "#DD9828" }}
                                    onClick={() => openDisableModal(item)}
                                  >
                                    <img
                                      src={`${process.env.PUBLIC_URL}/images/disable-user.png`}
                                      alt="Disable"
                                    />{" "}
                                    Disable Staff Profile
                                  </li>
                                )}

                                <li
                                  style={{ color: "red" }}
                                  onClick={() => openDeleteModal(item)}
                                >
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/staff-delete.png`}
                                    alt="Delete"
                                  />{" "}
                                  Delete Profile
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div className="pagination staff-pagination">
            <div style={{ display: "flex" }}>
              {/* Previous button */}
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || loading || !paginationData.prev_page_url}
                aria-label="Previous page"
              >
                {loading ? (
                  <span className="loading-spinner" aria-hidden="true">
                    ⏳
                  </span> // Spinner during loading
                ) : (
                  "<" // Left arrow for previous
                )}
              </button>

              {/* Display page numbers */}
              <div className="page-numbers">
                {generatePageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => handlePageClick(page)}
                    disabled={page === "..." || loading}
                    className={page === currentPage ? "active" : ""}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Next button */}
              <button
                onClick={goToNextPage}
                disabled={
                  currentPage === paginationData.last_page || loading || !paginationData.next_page_url
                }
                aria-label="Next page"
              >
                {loading ? (
                  <span className="loading-spinner" aria-hidden="true">
                    ⏳
                  </span> // Spinner during loading
                ) : (
                  ">"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddStaffModal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        imagePreviewUrl={imagePreviewUrl}
      />

      <StaffStatusModal
        isOpen={successModalIsOpen}
        onRequestClose={() => setSuccessModalIsOpen(false)}
        type="success"
        title="Successful"
        description="Staff has been added successfully."
        showCredentials={true}
        loginId={loginId}
        password={password}
        primaryButtonText="Share"
        secondaryButtonText="Back"
        onPrimaryClick={handleShare}
        onSecondaryClick={() => {
          setSuccessModalIsOpen(false);
          window.location.reload();
        }}
        className="add-admin-modal2"
      />

      <ViewStaffModal
        isOpen={viewModalIsOpen}
        onRequestClose={() => {
          setViewModalIsOpen(false);
          setPasswordInput(""); // Reset password when modal closes
        }}
        selectedAdmin={selectedAdmin}
        passwordInput={passwordInput}
        handlePasswordChange={handlePasswordChange}
        handlePasswordSubmit={handlePasswordSubmit}
      />

      <EditStaffModal
        isOpen={editModalIsOpen}
        onRequestClose={() => setEditModalIsOpen(false)}
        selectedAdmin={selectedAdmin}
        formData={formData}
        handleChange={handleChange}
        handleEditSubmit={handleEditSubmit}
      />

      <DeleteStaffModal
        isOpen={deleteModalIsOpen}
        onRequestClose={() => setDeleteModalIsOpen(false)}
        selectedAdmin={selectedAdmin}
        handleDeleteAdmin={handleDeleteAdmin}
      />

      <StaffStatusModal
        isOpen={deleteSuccessModalIsOpen}
        onRequestClose={() => setDeleteSuccessModalIsOpen(false)}
        type="success"
        title="User Profile Deleted"
        description="Staff profile has been deleted successfully"
        primaryButtonText="Cancel"
      />

      <ViewStaffInfoModal
        isOpen={passwordSuccessModalIsOpen}
        onRequestClose={() => setPasswordSuccessModalIsOpen(false)}
        selectedAdmin={selectedAdmin}
        viewStaffInfo={viewStaffInfo}
        handleShareDetails={handleShareDetails}
      />

      <StaffStatusModal
        isOpen={editSuccessModalIsOpen}
        onRequestClose={() => setEditSuccessModalIsOpen(false)}
        type="success"
        title="Successful"
        description="Staff profile has successfully been updated"
        primaryButtonText="Cancel"
      />

      <EnableStaffModal
        isOpen={enableModalIsOpen}
        onRequestClose={() => setEnableModalIsOpen(false)}
        selectedAdmin={selectedAdmin}
        handleEnableUser={handleEnableUser}
      />

      <StaffStatusModal
        isOpen={enableSuccessModalIsOpen}
        onRequestClose={() => setEnableSuccessModalIsOpen(false)}
        type="success"
        title="User Profile Enabled"
        description="Staff profile has been Enabled"
        primaryButtonText="Cancel"
      />

      <DisableStaffModal
        isOpen={disableModalIsOpen}
        onRequestClose={() => setDisableModalIsOpen(false)}
        selectedAdmin={selectedAdmin}
        handleDisableUser={handleDisableUser}
      />

      <StaffStatusModal
        isOpen={disableSuccessModalIsOpen}
        onRequestClose={() => setDisableSuccessModalIsOpen(false)}
        type="success"
        title="User Profile Disabled"
        description="Staff profile has been Disabled"
        primaryButtonText="Cancel"
      />
    </div>
  );
}

export default StaffManagement;
